import type { Item3D } from '../types/optimization'
import { convertMeasurement, parseMeasurementUnit } from './measurement'

type CargoPreset = Record<string, any>

/** Build an independent cargo line from a catalog product/preset. */
export function createCargoItemFromPreset(
  preset: CargoPreset,
  existingItems: Item3D[],
  createId: () => string,
  fallbackColor: string,
): Item3D {
  const maxOrder = existingItems.reduce((max, item) => Math.max(max, Number(item.unloading_order) || 0), 0)
  const dimensionUnit = parseMeasurementUnit(preset.dimension_unit || 'mm')
  const weightUnit = parseMeasurementUnit(preset.weight_unit || 'kg')
  convertMeasurement(1, dimensionUnit, 'mm')
  convertMeasurement(1, weightUnit, 'kg')
  const positive = (field: string) => {
    const value = Number(preset[field])
    if (!Number.isFinite(value) || value <= 0) throw new Error(`${field} doit être une mesure positive`)
    return value
  }
  const weight = Number(preset.weight ?? 0)
  if (!Number.isFinite(weight) || weight < 0) throw new Error('weight doit être une mesure positive ou nulle')

  return {
    id: createId(),
    product_id: preset.id != null ? String(preset.id) : null,
    sku: preset.sku || (preset.id != null ? String(preset.id) : null),
    product_name: preset.name,
    product_description: preset.description || null,
    description: preset.name || preset.description || '',
    hs_code: preset.hs_code,
    unit_of_measure: preset.unit_of_measure,
    packaging_type: preset.packaging_type,
    volume: preset.volume,
    volume_unit: preset.volume_unit,
    country_of_origin: preset.country_of_origin,
    status: preset.status,
    favorite_rating: preset.favorite_rating,
    length: positive('length'), width: positive('width'), height: positive('height'),
    weight, weight_unit: weightUnit, dimension_unit: dimensionUnit,
    quantity: Number(preset.quantity) > 0 ? Number(preset.quantity) : 1,
    stackable: preset.stackable ?? true,
    fragile: preset.fragile ?? false,
    rotatable: preset.rotatable ?? true,
    requires_pallet: preset.requires_pallet ?? false,
    pallet_type: preset.pallet_type || undefined,
    unloading_order: maxOrder + 1,
    color: preset.color || fallbackColor,
    _dndId: createId(),
    rotationOptions: {
      allowRotationX: preset.allow_rotation_x ?? true,
      allowRotationY: preset.allow_rotation_y ?? true,
      allowRotationZ: preset.allow_rotation_z ?? true,
      rotationStrategy: preset.rotation_strategy || 'DEFAULT_6_ROTATIONS',
    },
    no_stack_above: preset.no_stack_above,
    keep_upright: preset.keep_upright,
    must_be_on_floor: preset.must_be_on_floor,
    keep_together_group: preset.keep_together_group,
    min_support_ratio: preset.min_support_ratio,
    center_of_gravity_x: preset.center_of_gravity_x,
    center_of_gravity_y: preset.center_of_gravity_y,
    center_of_gravity_z: preset.center_of_gravity_z,
    max_stack_weight: preset.max_stack_weight,
    max_stack_layer: preset.max_stack_layer,
    max_pallet_height_mm: preset.max_pallet_height_mm,
    pallet_custom_length: preset.pallet_custom_length,
    pallet_custom_width: preset.pallet_custom_width,
    max_items_per_pallet: preset.max_items_per_pallet,
    pallet_board_height_mm: preset.pallet_board_height_mm,
    pallet_empty_weight_kg: preset.pallet_empty_weight_kg,
    pallet_color: preset.pallet_color,
    max_pallet_weight_kg: preset.max_pallet_weight_kg,
    dangerous_goods: preset.dangerous_goods ?? preset.is_dangerous_goods,
    is_hazardous: preset.dangerous_goods ?? preset.is_dangerous_goods,
    adr_class: preset.adr_class ?? preset.dangerous_goods_class,
    un_number: preset.un_number,
    temperature_controlled: preset.temperature_controlled,
    min_temperature: preset.min_temperature,
    max_temperature: preset.max_temperature,
    temperature_unit: preset.temperature_unit,
  } as Item3D
}
